import React, {useMemo} from 'react';
import {Page} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {filterHierarchy} from "../../util/EmployeeHierarchy";
import {CompanyTreemap} from "./CompanyTreemap";
import {CompanyTreemapFilter, companyTreemapFilterFormSerializer} from "./CompanyTreemapFilter";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import {useFilter} from "../../util/FilterSerializer";
import {useEmployeeNeighbourhoodStore} from "../../api/EmployeeNeighbourhoodStore";
import {Neighbourhood} from "../../api/Model";
import {RemoteValue} from "../../util/RemoteValue";
import {useResidenceStore} from "../../api/ResidenceStore";


export default function CompanyTreemapPage({neighbourhoods}: {
    neighbourhoods: Neighbourhood[]
}) {
    const employee2neighbourhood = useEmployeeNeighbourhoodStore(state => state.remoteValue);
    const employeeHierarchy = useEmployeeHierarchyStore(employee2neighbourhood);
    const residencyModel = useResidenceStore().remoteValue;
    const filterSerializer = useMemo(
        () => companyTreemapFilterFormSerializer(neighbourhoods),
        [neighbourhoods],
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const hierarchy = useMemo(() => (
        RemoteValue
            .combine({employeeHierarchy, residencyModel})
            .map(({employeeHierarchy, residencyModel}) => {
                return filterHierarchy(
                    employeeHierarchy.globalHierarchy,
                    node => node.type === 'node' && residencyModel.isResident(node) && !node.deactivated,
                );
            })
    ), [employeeHierarchy, residencyModel]);

    return (
        <Page
            header="Company Treemap"
            description={"This treemap visualization shows the management hierarchy of employees at the Berlin office.\n" +
                "Each box represents an employee, with managers containing their direct reports.\n" +
                "Hover over a box to see more details about the employee."}
            filter={<CompanyTreemapFilter filter={filter} setFilter={setFilter}/>}
        >
            <LoadingData
                value={hierarchy}
                emptyMessage="No employees found."
                defaultErrorMessage="Failed to load employees data. Please try again later."
            >{hierarchy => (
                <CompanyTreemap
                    hierarchyData={hierarchy}
                    filter={filter}
                />
            )}</LoadingData>
        </Page>
    );
}
